import { useState } from 'react'
import type { InvoiceSortBy, SortOrder } from '@/types'
import { SortableHead } from '@/components/sortable-head'
import { PERIOD_PRESETS, periodRange, type PeriodPreset } from '@/lib/period-presets'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon } from 'lucide-react'
import {
	Badge,
	Button,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	useListContactsQuery,
	useListFinanceTransactionsQuery,
	useListPropertiesQuery,
} from '@/api'
import { CreateTransactionDialog } from '@/components/create-transaction-dialog'
import { PanelDown } from '@/components/panel-down'
import { CURRENCY_SIGNS, DEFAULT_LOCALE, PAYMENT_METHOD_KEYS } from '@/types'

export const Route = createFileRoute('/_app/finances/transactions')({
	component: FinancesTransactionsPage,
})

const PAGE_SIZE = 10

const dateFormatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })

function formatDate(iso: string | null | undefined) {
	if (!iso) return '—'
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return '—'
	return dateFormatter.format(d)
}

function formatAmount(n: number, currency: string) {
	return `${n.toLocaleString(DEFAULT_LOCALE)} ${CURRENCY_SIGNS[currency] ?? currency}`
}

function FinancesTransactionsPage() {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [propertyFilter, setPropertyFilter] = useState('')
	const [tenantFilter, setTenantFilter] = useState('')
	// Server-side, both of them: the list is paginated by the server, so a
	// browser-side sort would reorder only the page on screen.
	const [period, setPeriod] = useState<PeriodPreset>('all')
	const [sortBy, setSortBy] = useState<InvoiceSortBy | undefined>()
	const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
	const [createOpen, setCreateOpen] = useState(false)

	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const { data: contactsData } = useListContactsQuery({ type: 'TENANT', limit: 200 })
	const { data } = useListFinanceTransactionsQuery({
		propertyId: propertyFilter || undefined,
		tenantId:   tenantFilter   || undefined,
		search:     search          || undefined,
		...periodRange(period),
		sortBy,
		sortOrder: sortBy ? sortOrder : undefined,
		page,
		limit: PAGE_SIZE,
	})

	function toggleSort(field: InvoiceSortBy) {
		if (sortBy === field) {
			setSortOrder((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortBy(field)
			setSortOrder('asc')
		}
		setPage(1)
	}

	const transactions = data?.transactions ?? []
	const total = data?.total ?? 0
	const properties = propertiesData?.properties ?? []
	const tenants = contactsData?.items ?? []

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Select
							value={propertyFilter || 'all'}
							onValueChange={(v) => { setPropertyFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.rental')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allRentals')}</SelectItem>
								{properties.map((p) => (
									<SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={tenantFilter || 'all'}
							onValueChange={(v) => { setTenantFilter(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.tenant')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allTenants')}</SelectItem>
								{tenants.map((c) => (
									<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={period}
							onValueChange={(v) => { setPeriod(v as PeriodPreset); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('finances.filters.period')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('finances.filters.allPeriods')}</SelectItem>
								{PERIOD_PRESETS.map((preset) => (
									<SelectItem key={preset} value={preset}>
										{t(`finances.filters.periods.${preset}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('finances.search')}
							value={search}
							className="w-[200px]"
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						<Button size="sm" onClick={() => setCreateOpen(true)}>
							{t('finances.createTransaction')}
						</Button>
					</>
				}
			/>

			{transactions.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('finances.emptyTransactions')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('finances.transactions.columnInvoice')}
											</Button>
										</TableHead>
										{/* The period is the invoice's — a transaction has
										    none of its own, so a deposit with no invoice sorts
										    to the end and drops out of a period filter. */}
										<SortableHead
											field="PERIOD"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
											className="w-[140px]"
										>
											{t('finances.table.period')}
										</SortableHead>
										<SortableHead
											field="PROPERTY"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
										>
											{t('finances.table.rental')}
										</SortableHead>
										<TableHead className="w-[120px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('finances.table.tenant')}
											</Button>
										</TableHead>
										<SortableHead
											field="AMOUNT"
											activeField={sortBy}
											order={sortOrder}
											onSort={toggleSort}
											className="w-[120px]"
										>
											{t('finances.table.amount')}
										</SortableHead>
										<TableHead className="w-[120px] font-[500] text-(--color-muted-foreground)">
											{t('finances.transactions.columnPayment')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{transactions.map((tx) => (
										<TableRow key={tx.id} className="h-[72px]">
											<TableCell className="px-4">
												<div className="flex flex-col gap-0.5">
													<span className="text-sm font-[500] text-(--color-foreground)">{tx.invoiceTitle ?? '—'}</span>
													<span className="text-sm text-(--color-muted-foreground)">{formatDate(tx.recordedAt)}</span>
												</div>
											</TableCell>
											<TableCell className="w-[140px] px-4 text-sm text-(--color-foreground)">
												{tx.periodStart && tx.periodEnd ? (
													<div>
														{formatDate(tx.periodStart)} -<br />{formatDate(tx.periodEnd)}
													</div>
												) : '—'}
											</TableCell>
											<TableCell className="px-4">
												<div className="flex flex-col gap-0.5">
													<span className="text-sm font-[500] text-(--color-info) truncate cursor-pointer underline-offset-2 hover:underline">
														{tx.propertyTitle ?? '—'}
													</span>
													<span className="text-sm text-(--color-muted-foreground)">
														{tx.periodStart && tx.periodEnd
															? `${formatDate(tx.periodStart)} — ${formatDate(tx.periodEnd)}`
															: '—'}
													</span>
												</div>
											</TableCell>
											<TableCell className="w-[120px] px-4 text-sm text-(--color-foreground)">
												{tx.tenantName ?? '—'}
											</TableCell>
											<TableCell className="w-[120px] px-4 text-sm text-(--color-foreground)">
												{formatAmount(Number(tx.amount), tx.currency)}
											</TableCell>
											<TableCell className="w-[120px] px-4">
												<Badge
													variant={tx.paymentMethod === 'CASH' ? 'secondary' : 'default'}
													size="sm"
												>
													{t(PAYMENT_METHOD_KEYS[tx.paymentMethod])}
												</Badge>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={total} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}
			<CreateTransactionDialog open={createOpen} onOpenChange={setCreateOpen} />
		</div>
	)
}
